import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { InventoryItem, InventoryUpdate } from '../types';
import { fetchInventory, addInventoryBatch, deleteInventoryItem, scanInventory, parseInventoryInput } from '../services/api';


interface InventoryActions {
    loadInventory: (token: string) => Promise<void>;
    setNewItem: (item: InventoryItem) => void;
    handleAddItem: (token: string) => Promise<InventoryItem[]>;
    handleDeleteItem: (token: string, name: string) => Promise<InventoryItem[]>;
    handleScanInventory: (token: string, files: File[]) => Promise<void>;
    handleManualParse: (token: string, text: string) => Promise<void>;
    handleStockKitchen: (token: string) => Promise<InventoryItem[]>;
    handleAutoInventoryUpdate: (updates: InventoryUpdate) => string;
    updateDraftQty: (name: string, qty: string) => void;
    removeFromDraft: (name: string) => void;
    setInventory: (items: InventoryItem[]) => void;
    setInventoryDraft: (items: InventoryItem[]) => void;
    reset: () => void;
}

interface InventoryStore extends InventoryActions {
    inventory: InventoryItem[];
    inventoryDraft: InventoryItem[];
    newItem: InventoryItem;
    isAdding: boolean;
    updatedItems: string[];
    isLoading: boolean;
}

const initialState = {
    inventory: [],
    inventoryDraft: [],
    newItem: { name: '', qty: '' },
    isAdding: false,
    updatedItems: [],
    isLoading: false,
};

export const useInventoryStore = create<InventoryStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            reset: () => set(initialState),

            setInventory: (items) => set({ inventory: items }),

            setInventoryDraft: (items) => set({ inventoryDraft: items }),

            loadInventory: async (token) => {
                set({ isLoading: true });
                try {
                    const inv = await fetchInventory(token);
                    set({ inventory: inv });
                } catch (err) {
                    console.error("Failed to load inventory:", err);
                } finally {
                    set({ isLoading: false });
                }
            },

            setNewItem: (item) => set({ newItem: item }),

            handleAddItem: async (token) => {
                const { newItem, inventory } = get();
                if (!newItem.name.trim()) return;

                set({ isAdding: true });
                try {
                    let itemsToAdd: InventoryItem[] = [];

                    try {
                        const parsed = await parseInventoryInput(token, newItem.name);
                        if (parsed.items && parsed.items.length > 0) {
                            itemsToAdd = parsed.items;
                        } else {
                            itemsToAdd = [{ name: newItem.name, qty: '1 unit' }];
                        }
                    } catch (err) {
                        console.warn("LLM parsing failed, using simple fallback", err);
                        itemsToAdd = [{ name: newItem.name, qty: '1 unit' }];
                    }

                    await addInventoryBatch(token, itemsToAdd);

                    const newInv = [...inventory];
                    itemsToAdd.forEach(added => {
                        const existingIdx = newInv.findIndex(i => i.name.toLowerCase() === added.name.toLowerCase());
                        if (existingIdx >= 0) {
                            newInv[existingIdx] = added;
                        } else {
                            newInv.push(added);
                        }
                    });

                    set({ inventory: newInv, newItem: { name: '', qty: '' } });
                    return newInv;
                } catch (err) {
                    console.error("Failed to add items:", err);
                    throw err;
                } finally {
                    set({ isAdding: false });
                }
            },

            handleDeleteItem: async (token, name) => {
                try {
                    await deleteInventoryItem(token, name);
                    const newInv = get().inventory.filter((item: InventoryItem) => item.name !== name);
                    set({ inventory: newInv });
                    return newInv;
                } catch (err) {
                    console.error(`Failed to delete ${name}`, err);
                    throw err;
                }
            },

            handleScanInventory: async (token, files) => {
                set({ isLoading: true });
                try {
                    const results = await scanInventory(token, files);
                    if (results.detected) {
                        set((state) => ({
                            inventoryDraft: [...state.inventoryDraft, ...results.detected]
                        }));
                    }
                } catch (err) {
                    console.error("Scan failed:", err);
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            handleManualParse: async (token, text) => {
                set({ isLoading: true });
                try {
                    const result = await parseInventoryInput(token, text);
                    if (result.items) {
                        set((state) => ({
                            inventoryDraft: [...state.inventoryDraft, ...result.items]
                        }));
                    }
                } catch (err) {
                    console.error("Manual parse failed:", err);
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            handleStockKitchen: async (token) => {
                const { inventoryDraft } = get();
                if (inventoryDraft.length === 0) return null;

                set({ isLoading: true });
                try {
                    await addInventoryBatch(token, inventoryDraft);
                    set({ inventoryDraft: [] });
                    const freshInv = await fetchInventory(token);
                    set({ inventory: freshInv });
                    return freshInv;
                } catch (err) {
                    console.error("Batch commitment failed:", err);
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            handleAutoInventoryUpdate: (updates) => {
                const { inventoryDraft } = get();
                const draftMap = new Map((inventoryDraft || []).map(i => [i.name.toLowerCase(), i.qty]));

                updates.items?.forEach(item => {
                    const name = item.name.toLowerCase();
                    if (item.remove) {
                        draftMap.delete(name);
                    } else {
                        // Additive logic for updates (FEAT-003)
                        const currentQty = draftMap.get(name) || "0";
                        const currentNum = parseInt(currentQty) || 0;
                        const newNum = parseInt(item.qty) || 0;
                        if (newNum > 0) {
                            const unit = item.qty.replace(/[0-9\s]/g, '').trim();
                            draftMap.set(name, `${currentNum + newNum}${unit ? ' ' + unit : ''}`.trim());
                        } else {
                            draftMap.set(name, item.qty);
                        }
                    }
                });

                const newDraft = Array.from(draftMap.entries()).map(([name, qty]) => ({ name, qty }));
                const updatedNames = updates.items?.map(i => i.name.toLowerCase()) || [];

                set({
                    inventoryDraft: newDraft,
                    updatedItems: updatedNames
                });

                setTimeout(() => {
                    if (get().updatedItems === updatedNames) {
                        set({ updatedItems: [] });
                    }
                }, 2000);

                return "Draft updated!";
            },

            updateDraftQty: (name, qty) => {
                set((state) => ({
                    inventoryDraft: state.inventoryDraft.map(i =>
                        i.name === name ? { ...i, qty } : i
                    )
                }));
            },

            removeFromDraft: (name) => {
                set((state) => ({
                    inventoryDraft: state.inventoryDraft.filter(i => i.name !== name)
                }));
            }
        }),
        { name: 'InventoryStore' }
    )
);
